import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  MdAdd,
  MdAutorenew,
  MdCheckCircle,
  MdClose,
  MdContentCopy,
  MdDeleteOutline,
  MdHandshake,
  MdHowToReg,
  MdVisibility,
  MdVisibilityOff,
  MdVpnKey,
} from "react-icons/md";
import ProjectLogo from "../../../Components/ProjectLogo/ProjectLogo";
import {
  createRm,
  deleteRm,
  generatePassword,
  listRms,
  resetRmPassword,
  suggestUsername,
} from "../../../utils/rms";
import "../projects/ProjectsView.css";
import "./RmView.css";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : null;

const plural = (count, word) => `${count.toLocaleString("en-IN")} ${word}${count === 1 ? "" : "s"}`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Couldn't copy — select the text instead");
  }
};

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  username: "",
  usernameEdited: false,
  password: "",
  showPassword: true,
};

// "Rahul" + "Sharma" -> "Rahul Sharma" (stored as the account's name)
const fullName = ({ firstName, lastName }) => `${firstName.trim()} ${lastName.trim()}`.trim();

// RM (relationship manager) accounts. The admin creates a login for each RM;
// the RM signs in on the normal login page and registers visitors, and those
// registrations carry the RM's name (the "Created By" column).
const RmView = ({ searchQuery = "" }) => {
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [freshId, setFreshId] = useState(null);

  // null | { mode: "add" } | { mode: "reset", rm } | { mode: "remove", rm }
  //      | { mode: "done", heading, name, username, password }
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const result = await listRms();
      setRms(result.data || []);
      setLoadError("");
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const query = searchQuery.trim().toLowerCase();
  const shown = useMemo(
    () =>
      query
        ? rms.filter(
            (rm) => rm.name.toLowerCase().includes(query) || rm.username.toLowerCase().includes(query)
          )
        : rms,
    [rms, query]
  );
  const totalVisitors = rms.reduce((sum, rm) => sum + rm.visitors, 0);

  const close = () => !saving && setModal(null);
  const openAdd = () => {
    setForm({ ...EMPTY_FORM, password: generatePassword() });
    setModal({ mode: "add" });
  };
  const openReset = (rm) => {
    setForm({ ...EMPTY_FORM, password: generatePassword() });
    setModal({ mode: "reset", rm });
  };

  // Username follows the name as first.last until the admin edits it
  const setNamePart = (field) => (event) =>
    setForm((f) => {
      const next = { ...f, [field]: event.target.value };
      return { ...next, username: f.usernameEdited ? f.username : suggestUsername(fullName(next)) };
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "add") {
        const result = await createRm({
          name: fullName(form),
          username: form.username,
          password: form.password,
        });
        toast.success(result.message);
        setFreshId(result.data.id);
        setModal({
          mode: "done",
          heading: "Account created",
          name: result.data.name,
          username: result.data.username,
          password: form.password,
        });
      } else if (modal.mode === "reset") {
        const result = await resetRmPassword(modal.rm.id, form.password);
        toast.success(result.message);
        setModal({
          mode: "done",
          heading: "Password reset",
          name: modal.rm.name,
          username: modal.rm.username,
          password: form.password,
        });
      } else if (modal.mode === "remove") {
        const result = await deleteRm(modal.rm.id);
        toast.success(result.message);
        setModal(null);
      }
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const formValid =
    modal?.mode === "reset"
      ? form.password.length >= 6
      : form.firstName.trim().length >= 2 &&
        /^[a-z0-9._@-]{3,40}$/.test(form.username) &&
        form.password.length >= 6;

  const passwordField = (
    <>
      <label htmlFor="rm-password">{modal?.mode === "reset" ? "New password" : "Password"}</label>
      <div className="rm-password">
        <input
          id="rm-password"
          type={form.showPassword ? "text" : "password"}
          value={form.password}
          minLength={6}
          maxLength={72}
          autoComplete="new-password"
          spellCheck={false}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
        />
        <button
          type="button"
          className="rm-password-btn"
          aria-label={form.showPassword ? "Hide password" : "Show password"}
          title={form.showPassword ? "Hide" : "Show"}
          onClick={() => setForm((f) => ({ ...f, showPassword: !f.showPassword }))}
        >
          {form.showPassword ? <MdVisibilityOff /> : <MdVisibility />}
        </button>
        <button
          type="button"
          className="rm-password-btn"
          aria-label="Generate a new password"
          title="Generate"
          onClick={() => setForm((f) => ({ ...f, password: generatePassword(), showPassword: true }))}
        >
          <MdAutorenew />
        </button>
      </div>
      <small className="rm-field-hint">At least 6 characters. You'll see it once more after saving.</small>
    </>
  );

  return (
    <div className="reservation-box rm-view">
      <div className="top projects-header">
        <div className="projects-heading">
          <h2>RM accounts</h2>
          <p>
            {loading
              ? "Loading…"
              : `${plural(rms.length, "account")} · ${plural(totalVisitors, "visitor")} registered`}
          </p>
        </div>
        <button type="button" className="pv-btn pv-btn--primary" onClick={openAdd}>
          <MdAdd /> Add RM
        </button>
      </div>

      <div className="rm-how">
        <MdHowToReg aria-hidden="true" />
        <p>
          Create a login for each relationship manager. They sign in on the normal login page and
          register visitors; each registration is credited to them under <strong>Created By</strong>.
        </p>
      </div>

      {loadError ? (
        <div className="projects-empty">
          <p>
            <strong>Couldn't load RM accounts.</strong> {loadError}
          </p>
          <button type="button" className="pv-btn pv-btn--ghost" onClick={load}>
            Try again
          </button>
        </div>
      ) : !loading && shown.length === 0 ? (
        <div className="projects-empty">
          <p>
            <strong>{query ? `No RM matches "${searchQuery.trim()}"` : "No RM accounts yet"}</strong>
          </p>
          <small>{query ? "Try another name or username." : "Add the first one with Add RM."}</small>
        </div>
      ) : (
        <ul className="rm-grid" aria-busy={loading}>
          {shown.map((rm, index) => (
            <li
              key={rm.id}
              className={`rm-card${rm.id === freshId ? " is-fresh" : ""}`}
              style={{ animationDelay: `${Math.min(index, 10) * 35}ms` }}
            >
              <div className="rm-card-head">
                <ProjectLogo name={rm.name} size={44} className="is-round" />
                <div className="rm-card-id">
                  <strong title={rm.name}>{rm.name}</strong>
                  <span title={rm.username}>{rm.username}</span>
                </div>
                <div className="rm-card-actions">
                  <button
                    type="button"
                    className="rm-icon-btn"
                    title="Reset password"
                    aria-label={`Reset password for ${rm.name}`}
                    onClick={() => openReset(rm)}
                  >
                    <MdVpnKey />
                  </button>
                  <button
                    type="button"
                    className="rm-icon-btn rm-icon-btn--danger"
                    title="Remove account"
                    aria-label={`Remove ${rm.name}'s account`}
                    onClick={() => setModal({ mode: "remove", rm })}
                  >
                    <MdDeleteOutline />
                  </button>
                </div>
              </div>

              <div className="rm-stats">
                <div className="rm-stat">
                  <span className="rm-stat-value">{rm.visitors.toLocaleString("en-IN")}</span>
                  <span className="rm-stat-label">Visitors</span>
                </div>
                <div className="rm-stat">
                  <span className="rm-stat-value">{rm.partners.toLocaleString("en-IN")}</span>
                  <span className="rm-stat-label">
                    <MdHandshake aria-hidden="true" /> Partners
                  </span>
                </div>
              </div>

              <div className="rm-card-foot">
                <span>
                  {rm.lastRegistration
                    ? `Last registration ${formatDate(rm.lastRegistration)}`
                    : "No registrations yet"}
                </span>
                {rm.createdAt && <span>Since {formatDate(rm.createdAt)}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal && (
        <div className="project-modal-backdrop" onClick={close}>
          <form
            className="project-modal rm-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={modal.mode === "done" ? (e) => { e.preventDefault(); setModal(null); } : handleSubmit}
            aria-labelledby="rm-modal-title"
          >
            <button type="button" className="project-modal-close" aria-label="Close" onClick={close} disabled={saving}>
              <MdClose />
            </button>

            {modal.mode === "done" ? (
              <>
                <div className="rm-done-icon">
                  <MdCheckCircle />
                </div>
                <h3 id="rm-modal-title">{modal.heading}</h3>
                <p>
                  Share these login details with <strong>{modal.name}</strong>. The password is stored
                  encrypted, so this is the last time it's shown.
                </p>
                <div className="rm-credentials">
                  {[
                    ["Username", modal.username],
                    ["Password", modal.password],
                  ].map(([label, value]) => (
                    <div key={label} className="rm-credential">
                      <span>{label}</span>
                      <code>{value}</code>
                      <button type="button" className="rm-icon-btn" aria-label={`Copy ${label.toLowerCase()}`} title="Copy" onClick={() => copyText(value)}>
                        <MdContentCopy />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="project-modal-actions">
                  <button
                    type="button"
                    className="pv-btn pv-btn--ghost"
                    onClick={() => copyText(`Site Registration login\nUsername: ${modal.username}\nPassword: ${modal.password}`)}
                  >
                    <MdContentCopy /> Copy both
                  </button>
                  <button type="submit" className="pv-btn pv-btn--primary" autoFocus>
                    Done
                  </button>
                </div>
              </>
            ) : modal.mode === "remove" ? (
              <>
                <div className="project-modal-preview">
                  <ProjectLogo name={modal.rm.name} size={64} className="is-round" />
                </div>
                <h3 id="rm-modal-title">Remove {modal.rm.name}?</h3>
                <p>
                  <strong>{modal.rm.username}</strong> will no longer be able to log in.
                  {modal.rm.visitors + modal.rm.partners > 0
                    ? ` Their ${plural(modal.rm.visitors + modal.rm.partners, "registration")} stay on record.`
                    : ""}
                </p>
                <div className="project-modal-actions">
                  <button type="button" className="pv-btn pv-btn--ghost" onClick={close} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="pv-btn pv-btn--danger" disabled={saving} autoFocus>
                    {saving ? "Removing…" : "Remove account"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="project-modal-preview">
                  <ProjectLogo
                    name={(modal.mode === "reset" ? modal.rm.name : fullName(form)) || "New RM"}
                    size={64}
                    className="is-round"
                  />
                </div>
                <h3 id="rm-modal-title">
                  {modal.mode === "reset" ? `Reset password` : "Add an RM"}
                </h3>
                <p>
                  {modal.mode === "reset"
                    ? `Set a new password for ${modal.rm.name} (${modal.rm.username}). Their old password stops working.`
                    : "They'll log in with this username and password to register visitors."}
                </p>

                {modal.mode === "add" && (
                  <>
                    <div className="rm-name-row">
                      <div>
                        <label htmlFor="rm-first-name">First name</label>
                        <input
                          id="rm-first-name"
                          autoFocus
                          value={form.firstName}
                          maxLength={30}
                          placeholder="e.g. Rahul"
                          autoComplete="off"
                          onChange={setNamePart("firstName")}
                        />
                      </div>
                      <div>
                        <label htmlFor="rm-last-name">
                          Last name <span className="rm-optional">optional</span>
                        </label>
                        <input
                          id="rm-last-name"
                          value={form.lastName}
                          maxLength={29}
                          placeholder="e.g. Sharma"
                          autoComplete="off"
                          onChange={setNamePart("lastName")}
                        />
                      </div>
                    </div>
                    <small className="rm-field-hint">
                      {fullName(form)
                        ? `Registrations will show “${fullName(form)}” under Created By.`
                        : "Shown as “Created By” on their registrations."}
                    </small>

                    <label htmlFor="rm-username">Username</label>
                    <input
                      id="rm-username"
                      value={form.username}
                      maxLength={40}
                      placeholder="e.g. rahul.sharma"
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          username: e.target.value.toLowerCase().replace(/\s/g, ""),
                          usernameEdited: true,
                        }))
                      }
                    />
                    <small className="rm-field-hint">Lowercase letters, numbers, dot, dash, underscore or @.</small>
                  </>
                )}

                {passwordField}

                <div className="project-modal-actions">
                  <button type="button" className="pv-btn pv-btn--ghost" onClick={close} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="pv-btn pv-btn--primary" disabled={saving || !formValid}>
                    {saving
                      ? "Saving…"
                      : modal.mode === "reset"
                      ? "Reset password"
                      : "Create account"}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default RmView;
